import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {enumerate} from 'ts-transformer-enumerate';
import {v4 as uuidV4} from 'uuid';
import _ from 'lodash';
import memoize from 'memoize-one';

import {Button} from 'primereact/button';
import {ContextMenu} from 'primereact/contextmenu';
import {Dialog} from 'primereact/dialog';
import {RadioButton} from 'primereact/radiobutton';
import {Toast} from 'primereact/toast';

import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';
import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import metaModel from '~/shared/src/utilities/metaModel';
import Item, {
    ItemAttributes,
    ItemAttributesForCreation
} from '~/shared/src/ci/Item';
import {
    ReferenceAttributes,
    ReferenceAttributesForCreation
} from '~/shared/src/ci/Reference';
import {
    LoadedItemData,
    LoadedReferenceData
} from '~/shared/src/utilities/loadedData';
import {extractDataObjectsFromDataStructure} from '~/shared/src/utilities/dataStructure';
import dataUtils from '~/shared/src/utilities/dataUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {
    rawModeSchema,
    showAdditionalRawNamesSchema
} from '~/shared/src/validationSchemes/modules/overview';
import {
    requestBaseParametersSchema,
    schemaSchema
} from '~/shared/src/model/BaseRequest';
import {
    CheckItemKeyUniquenessParameters,
    CheckItemKeyUniquenessResult,
    SaveDataResult
} from '~/shared/src/utilities/modules/data';
import {
    ClearConfigParameters,
    GetFormItemIdByDisplayAttributeResult
} from '~/shared/src/utilities/modules/form';

import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import Headerbar from '~/frontend/src/components/base/FormDesigner/Headerbar';
import DesignerTree, {
    ReferenceType as DesignerTreeReferenceType
} from '~/frontend/src/components/base/FormDesigner/DesignerTree';
import Toolbar from '~/frontend/src/components/base/FormDesigner/Toolbar';
import ResizeableLeftMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableLeftMenuPanel';
import ResizeableRightMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableRightMenuPanel';
import Form from '~/frontend/src/components/base/Form';
import FormDesigner, {
    DragData,
    DragOptions,
    Mode as FormDesignerMode,
    ReferenceType as FormDesignerReferenceType
} from '~/frontend/src/components/base/FormDesigner';
import AttributesForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';
import FieldWrapper from '~/frontend/src/components/base/shared/field/FieldWrapper';
import ItemTypeDropdown from '~/frontend/src/components/base/shared/field/ItemTypeDropdown';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';
import SchemaDropdown from '~/frontend/src/components/base/shared/field/SchemaDropdown';

import {
    isDataModified,
    isDataModifiedRecursive,
    MergeMode
} from '~/frontend/src/utilities/dataUtils';
import {
    getPanelIcon as getPanelIconOrig,
    getPanelTitle as getPanelTitleOrig,
    ItemTitleConfig,
    setPanelInfo as setPanelInfoOrig
} from '~/frontend/src/utilities/panelInfo';
import {
    getPanelElusiveData,
    getPanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {FormPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/definitions';

import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {
    ElusiveDataState,
    PanelElusiveDataState
} from '~/frontend/src/reducers/elusiveData';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import loadData from '~/frontend/src/actions/data/loadData';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import * as TreeActions from '~/frontend/src/actions/panel/treeView';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {
    MsgParams,
    setMessage,
    unsetMessage
} from '~/frontend/src/actions/intercom';
import {
    clearElusiveData,
    clearElusiveDataForSubpanels,
    setElusiveData
} from '~/frontend/src/actions/elusiveData';
import * as CiActions from '~/frontend/src/actions/data';
import setParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import setPanelDirty, {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import setPanelInfo, {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import saveData, {SaveData} from '~/frontend/src/actions/panel/viewDesigner/saveData';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import addItem, {AddItem} from '~/frontend/src/actions/data/addItem';
import addReference, {AddReference} from '~/frontend/src/actions/data/addReference';
import {DeleteReference} from '~/frontend/src/actions/data/deleteReference';
import cancelRequests from '~/frontend/src/actions/ajax/cancelRequests';


type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    showCouldNotSavePopup? : boolean
};

type Variables = {
    [ variable : string ] : any;
};


const defaultProps = {
    /**
     * The database schema to filter data and metadata for.
     */
    dataContext: 'formDesigner' as string,
    //
    title: 'FormDesignerView' as string,
    //
    schema: 'DATA' as Schema,

    /**
     * The width of the menu on the left side in percent.
     */
    menuWidth: 15 as number,

    /**
     * The width of the menu on the right side in percent.
     */
    menu2Width: 20 as number,

    /**
     * The status to filter data for.
     */
    status: 'active' as Status,

    /**
     * The timestamp to filter data at.
     */
    timestamp: 'now' as string,

    /**
     * The timezone to filter data at.
     */
    timezone: 'Europe/Berlin' as string,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    mode: 'design' as FormDesignerMode,

    showHiddenLayoutElements: false as boolean,

    newLayoutElementsWidth: '100%' as string
};
type DefaultProps = Readonly<typeof defaultProps>;

/**
 * Definition of components props
 * @memberof TreeTest
 * @property {String} dataContext='cypher' The ci-data context of this component
 * @property {Object} items CI-Data as stored in redux store
 * @property {String} itemsVersion Version of CI-Data as stored in redux store
 */
type Props = {
    uid : string;

    /**
     * The ItemId to filter data and metadata for.
     */
    itemId? : number;

    // the ciId of the existing form
    rootItemId : number

    // the key of the new or existing form
    rootItemKey : string;

    // the name of the new or existing form
    rootItemName : string;

    // the id of the root item to display data in the form
    dataRootItemId? : number;

    payloadItemType? : string;
    payloadItemSchema? : Schema;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode? : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames? : boolean;

    selectedLayoutElementId? : number;

    referenceToCreateForNew? : ReferenceAttributesForCreation;

    /**
     * The props for sub panels
     */
    subPanelProps? : any;

    onSave? : ( rootItemId : number, dataContext : string ) => void;

    /**
     * The full elusiveData
     */
    allElusiveData? : ElusiveDataState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     * A storage for temporary needed data (not persisted to local storage via redux store persist), like
     *  - lastParams: The stringified parameters of the last loaded search. Used to determine if a new result have to be loaded from the backend.
     *  - gridData: The ordered itemIds to display in the grid.
     *  - totalRecords: The total result data count to be displayed in the DataTable.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;
};

const Actions = Object.assign( { loadData, setMessage, unsetMessage, setElusiveData, clearElusiveData, clearElusiveDataForSubpanels, setPanelInfo, setPanelDirty, addItem, addReference, closePanel }, PanelActions, TreeActions, IntercomActions, CiActions );

type SetParam = <K extends keyof ComponentProps>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {
    //updatePanelProps: typeof updatePanelProps;
    loadData : typeof loadData;
    setParam : SetParam;
    setParams : SetParams;
    unsetParam : UnsetParam;
    setMessage : typeof setMessage;
    unsetMessage : typeof unsetMessage;
    setPanelInfo : SetPanelInfo;
    setElusiveData : typeof setElusiveData;
    clearElusiveData : typeof clearElusiveData;
    clearElusiveDataForSubpanels : typeof clearElusiveDataForSubpanels;
    setPanelDirty : SetPanelDirty;
    deleteReference : DeleteReference;
    addItem : AddItem;
    addReference : AddReference;
};

type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
} & {
    closePanel : ClosePanel;
};

type AllProps = Props & DefaultProps & ActionProps;
export type ComponentProps = Partial<DefaultProps> & Props;

type State = {
    rootItemId : number;

    isDirty : boolean;
    errorCount : number;
    errorTooltip? : string | React.ReactNode | ( string | React.ReactNode )[];
    errorMessage? : string | React.ReactNode | ( string | React.ReactNode )[];
    rootItemFormState : 'CLEAR' | 'DIRTY' | 'ERROR';
    erronousLayoutElementIds : number[];

    /**
     * Counts up on menu scroll. Used to fire update for arrows
     */
    scrollIndicator : number;

    initialized : boolean;

    formItemTypeId : number;

    showCreateNewFormDialog : boolean;

    createNewFormDialogKey? : string;
    createNewFormDialogKeyError? : string;
    createNewFormDialogSchema : Schema;
    createNewFormDialogItemType? : string;

    /**
     * Whether ai is available for form generation (BackendAction:GenerateForm).
     */
    aiAvailable : boolean;
    /**
     * Id of the selected quick action in the create-new-form dialog (undefined = create default form).
     */
    selectedQuickActionId? : string;
    /**
     * Whether a form is currently being generated by the ai BackendAction.
     */
    generatingForm : boolean;

    mobile : boolean;
};


const schemata  = enumerate<YnSchema>(),
      getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches ? true : false;


/**
 * Playground to test Tree implementation
 */
class FormDesignerView extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static panelType = 'FormDesignerView';
    static panelTabTitle = 'YN.base.frontend.formDesigner.tabTitle';
    static panelTabIcon = 'itemTypeIcon:CFG:Form';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.frontend.formDesigner';
    static panelPropsSchema = Joi.object()
        // panelSchema defaults
        .concat( requestBaseParametersSchema )
        .concat( Joi.object( {
            panelType        : Joi.string().valid( FormDesignerView.panelType ),
            rootItemType     : Joi.string(),
            panelTabTitle    : Joi.string().default( FormDesignerView.panelTabTitle ),
            panelTabIcon     : Joi.string().default( FormDesignerView.panelTabIcon ),
            rootItemId       : Joi.number().description( 'Id of the form.' ),
            rootItemKey      : Joi.string().description( 'Key of form' ),
            payloadItemType  : Joi.string().description( 'Item Type of the payload root item (only needed for new forms)' ),
            payloadItemSchema: schemaSchema,
            menuWidth        : Joi.number()
                .default( 15 )
                .description( 'The width of the left menu panel in percent of the available width.' ),
            menu2Width       : Joi.number()
                .default( 30 )
                .description( 'The width of the right menu panel in percent of the available width.' ),
            // this is only for compatibility with ItemFinder overview (if we open this panel from there)
            rawMode                : rawModeSchema,
            showAdditionalRawNames : showAdditionalRawNamesSchema,
            title                  : Joi.string(),
            referenceToCreateForNew: Joi.object( {
                srcItemId     : Joi.number()
                    .required(),
                destItemId    : Joi.number()
                    .required(),
                referenceSetId: Joi.number()
                    .required(),
                statusId      : Joi.number()
                    .required()
            } ),
            onSave                 : Joi.function()
        } ) );

    static aiChatCompletionKey = 'BackendAction:generateForm';

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

            // abort running request
            dispatch( cancelRequests( props.uid ) );

            // clear global data
            if ( props.dataContext ) {
                delete global.data[ props.dataContext ];
                delete global.data[ `${props.dataContext}-meta` ];
                delete global.data[ `${props.dataContext}_data` ];
                delete global.data[ `${props.dataContext}_DataObjects` ];
            }

            // clear backend data of all loaded forms
            for ( const subPanelProps of Object.values( props.subPanelProps ?? {} ) as any[] ) {
                if ( subPanelProps.moduleTransformer === 'form' && subPanelProps.configHash ) {
                    const params : ClearConfigParameters = {
                        configHash: subPanelProps.configHash
                    };

                    await xhr.post( `${BASE_URL}/api/form/clearConfig`, params, undefined, props.uid, props.uid, dispatch );
                }
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await FormDesignerView.savePanelData(
                dispatch,
                getState,
                props
            );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await FormDesignerView.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : Pick<AllProps, 'dataContext' | 'rootItemId' | 'rootItemKey'> ) : string | undefined {
        const {
                  dataContext,
                  rootItemId,
                  rootItemKey
              }      = props,
              itemId = rootItemId
                  ?? dataUtils.findItem( dataContext, 'Form', rootItemKey )?.id
                  ?? 0,
              item   = global.data[ dataContext ]?.items?.[ itemId ];

        let retVal : string | undefined;

        if ( item ) {
            retVal = `#${FormDesignerView.panelType}/${encodeURIComponent( item.displayAttributeValue )}`;
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = rootItem.displayAttributeValue
        if ( params?.[ 1 ] ) {
            const displayAttributeValue = decodeURIComponent( params[ 1 ] );

            // resolve viewKey and rootItemId
            const res    = await xhr.post(
                      `${BASE_URL}/api/form/getFormItemIdByDisplayAttribute`,
                      {
                          displayAttributeValue
                      }
                  ),
                  result = res?.data as GetFormItemIdByDisplayAttributeResult;

            if ( result ) {
                if ( result.data?.rootItemId ) {
                    retVal.rootItemId = result.data.rootItemId;
                }
                else {
                    stackError.add( new LocalizableError( 'YN.base.designer.resolvePanelPropsFromURL.error.couldNotResolveRootItemId', { attribute: displayAttributeValue } ) );
                }
            }
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.designer.resolvePanelPropsFromURL.error.couldNotResolveRootItem' ) );
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        return retVal;
    }

    static async doSaveData( uid : string, dispatch : AppDispatch, successMsgParams : MsgParams | undefined = undefined ) : ReturnType<ReturnType<SaveData>> {
        const props                                              = getPanelProps( uid )?.panelProps as ComponentProps,
              elusiveData                                        = getPanelElusiveData( uid ),
              propsWithDefaults : Required<DefaultProps> & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  onSave,
                  rootItemId,
                  selectedLayoutElementId,
                  subPanelProps
              }                                                  = propsWithDefaults,
              rootItemIdForSave                                  = rootItemId ?? elusiveData?.data?.rootItemId,
              result                                             = await dispatch(
                  saveData(
                      uid,
                      'CFG',
                      rootItemIdForSave,
                      undefined,
                      undefined,
                      undefined,
                      successMsgParams
                  )
              );

        // remap selectedLayoutElement id
        if ( selectedLayoutElementId && result && result.data?.itemMetaDataMapping?.[ String( selectedLayoutElementId ) as unknown as number ] ) {
            dispatch( setParam( uid, 'selectedLayoutElementId', result.data.itemMetaDataMapping[ String( selectedLayoutElementId ) as unknown as number ].id ) );
        }

        // remap activeTabContainerIndexes
        if ( result && result.data?.referenceMetaDataMapping && Object.keys( result.data.referenceMetaDataMapping ).length ) {
            for ( const subPanelKey of Object.keys( subPanelProps ) as string[] ) {
                if ( subPanelProps[ subPanelKey ].activeTabContainerIndexes ) {
                    const activeTabContainerIndexes    = subPanelProps[ subPanelKey ].activeTabContainerIndexes,
                          newActiveTabContainerIndexes = { ...activeTabContainerIndexes };

                    let remapped : boolean = false;

                    for ( const realTabContainerLayoutElementId of Object.keys( newActiveTabContainerIndexes ) ) {
                        if ( newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] < 0 && result.data.referenceMetaDataMapping[ newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] ] ) {
                            remapped = true;
                            newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] = result.data.referenceMetaDataMapping[ newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] ].id;
                        }
                    }

                    if ( remapped ) {
                        dispatch( setParam(
                            `${uid}_${subPanelKey}`,
                            'activeTabContainerIndexes',
                            newActiveTabContainerIndexes
                        ) );
                    }
                }
            }
        }

        if ( onSave ) {
            // remap rootItem id
            if ( result && result.data?.itemMetaDataMapping?.[ String( rootItemIdForSave ) as unknown as number ] ) {
                onSave( result.data.itemMetaDataMapping[ String( rootItemIdForSave ) as unknown as number ].id, uid );
            }
            else {
                onSave( rootItemIdForSave, uid );
            }
        }

        return result;
    }

    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid
              }                                        = propsWithDefaults,
              allElusiveData                           = getState()?.elusiveData;

        let retVal : Promise<SaveDataResult | false> = Promise.resolve( false ),
            hasError : boolean                       = false;

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData;

                    if ( panelElusiveData.errorData?.count ) {
                        hasError = true;
                        break;
                    }
                }
            }
        }

        // panel has errors -> show popup
        if ( hasError ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );
        }

        // panel has no errors -> save
        else {
            retVal = FormDesignerView.doSaveData( uid, dispatch, successMsgParams );
        }

        return retVal;
    }

    menuContentPanelContentStyle = { padding: 0 };
    menuContentPanelsPt = {
        gutter: {
            style: {
                display: 'none'
            }
        }
    };

    isLoading : boolean = false;

    cm : InstanceType<typeof ContextMenu> | null = null;
    toast : Toast | null = null;
    tree : DesignerTreeReferenceType | null = null;
    formDesigner : FormDesignerReferenceType | null = null;

    getPanelIcon = getPanelIconOrig;
    getPanelTitle = getPanelTitleOrig;
    setPanelInfo = setPanelInfoOrig;

    /**
     * React Component constructor
     * @param {Object} props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            rootItemId: 0 as number,

            isDirty                 : false,
            errorCount              : 0,
            rootItemFormState       : 'CLEAR',
            erronousLayoutElementIds: [],

            scrollIndicator: 0 as number,

            initialized: false,

            formItemTypeId: metaModel[ 'CFG' ].getItemType( 'Form' )?.id ?? 0,

            showCreateNewFormDialog: false,

            createNewFormDialogKey     : props.rootItemKey ? props.rootItemKey : undefined,
            createNewFormDialogSchema  : props.payloadItemSchema ? props.payloadItemSchema : 'DATA',
            createNewFormDialogItemType: props.payloadItemType ? props.payloadItemType : undefined,

            aiAvailable          : false,
            selectedQuickActionId: undefined,
            generatingForm       : false,

            mobile: getMobile()
        };

        this.onSetSidePanelTreeVariables = this.onSetSidePanelTreeVariables.bind( this );

        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.getDesignerTree = memoize( this.getDesignerTree, this.getDesignerTreeEqual );
        this.getForm = memoize( this.getForm, this.getFormEqual );
        this.getFormDesigner = memoize( this.getFormDesigner, this.getFormDesignerEqual );
        this.getHeaderbar = memoize( this.getHeaderbar );
        this.getPanelIcon = memoize( this.getPanelIcon );
        this.getPanelTitle = memoize( this.getPanelTitle );
        this.getToolbar = memoize( this.getToolbar );
        this.setPanelInfo = memoize( this.setPanelInfo );
        this.getSelectableSchemata = memoize( this.getSelectableSchemata );
        this.getQuickActions = memoize( this.getQuickActions );

        this.checkKeyUniqueness = _.throttle( this.checkKeyUniqueness, 200, { trailing: true } );
        this.onMenuScroll = _.throttle( this.onMenuScroll, 100, { trailing: true } );

        this.checkForCreateNewFormPrerequisits = this.checkForCreateNewFormPrerequisits.bind( this );
        this.checkForAiAvailable = this.checkForAiAvailable.bind( this );
        this.onSelectQuickAction = this.onSelectQuickAction.bind( this );
        this.onCreateNewFormDialogHide = this.onCreateNewFormDialogHide.bind( this );
        this.onCreateNewFormDialogOkButtonClick = this.onCreateNewFormDialogOkButtonClick.bind( this );
        this.onCreateNewFormDialogSetItemType = this.onCreateNewFormDialogSetItemType.bind( this );
        this.onCreateNewFormDialogSetKey = this.onCreateNewFormDialogSetKey.bind( this );
        this.onCreateNewFormDialogSetSchema = this.onCreateNewFormDialogSetSchema.bind( this );
        this.onDataRootItemIdChange = this.onDataRootItemIdChange.bind( this );
        this.onMarkFieldsForAlias = this.onMarkFieldsForAlias.bind( this );
        this.onMarkLayoutElementsForAlias = this.onMarkLayoutElementsForAlias.bind( this );
        this.onMatchMobileChangeHandler = this.onMatchMobileChangeHandler.bind( this );
        this.onMenu2WidthChange = this.onMenu2WidthChange.bind( this );
        this.onMenuWidthChange = this.onMenuWidthChange.bind( this );
        this.onNewLayoutElementDragStart = this.onNewLayoutElementDragStart.bind( this );
        this.onNewLayoutElementsWidthChange = this.onNewLayoutElementsWidthChange.bind( this );
        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
        this.onSelectLayoutElement = this.onSelectLayoutElement.bind( this );
        this.onShowHiddenLayoutElementsChange = this.onShowHiddenLayoutElementsChange.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  uid,
                  rootItemId,
                  rootItemKey,
                  dataContext,
                  subPanelProps,
                  allElusiveData
              }                                              = props,
              erronousLayoutElementIds : number[]            = [],
              tooltipErrors : ( string | React.ReactNode )[] = [],
              messageErrors : ( string | React.ReactNode )[] = [];

        let isDirty : boolean                              = rootItemId ? isDataModifiedRecursive( dataContext, 'CFG', rootItemId ) : isDataModified( dataContext, 'CFG' ),
            errorCount                                     = 0,
            rootItemFormState : State['rootItemFormState'] = 'CLEAR';

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData,
                          panelKey         = elusiveDataPanelKey.replace( `${uid}_`, '' );

                    // only check direct sub forms
                    if ( panelKey.match( /^form-/ ) && panelKey.indexOf( '_' ) === -1 ) {
                        const panelProps   = subPanelProps?.[ panelKey ],
                              formRootItem = panelProps
                                  ? global.data[ dataContext ]?.items[ panelProps.rootItemId ]
                                  : undefined,
                              elementName  = formRootItem
                                  ? formRootItem.name
                                      ? formRootItem.name
                                      : formRootItem?.key
                                  : '',
                              elementType  = formRootItem ? formRootItem.itemType.label : 'Element';

                        isDirty = isDirty || !!panelElusiveData.isDirty;

                        if ( formRootItem?.id === rootItemId ) {
                            if ( panelElusiveData.errorData?.count ) {
                                rootItemFormState = 'ERROR';
                            }
                            else if ( panelElusiveData.isDirty ) {
                                rootItemFormState = 'DIRTY';
                            }
                        }

                        if ( panelElusiveData.errorData?.count ) {
                            if ( formRootItem?.id && formRootItem.id !== rootItemId ) {
                                erronousLayoutElementIds.push( formRootItem.id );
                            }

                            errorCount += panelElusiveData.errorData.count;

                            if ( panelElusiveData.errorData.tooltip?.length ) {
                                tooltipErrors.push(
                                    <>
                                        <div className="yn-tooltip-header2">
                                            {__( 'YN.base.frontend.formDesigner.error.tooltip.title', { type: elementType, name: elementName } )}
                                        </div>
                                        {panelElusiveData.errorData.tooltip}
                                    </>
                                );

                                if ( panelElusiveData.errorData.message?.length ) {
                                    messageErrors.push(
                                        <>
                                            <div className="yn-error-subtitle">
                                                {__( 'YN.base.frontend.formDesigner.error.message.title', { type: elementType, name: elementName } )}
                                            </div>
                                            <hr className="yn-error-ruler"/>
                                            <div className="yn-error-suberror">
                                                {panelElusiveData.errorData.message}
                                            </div>
                                        </>
                                    );
                                }
                            }
                        }
                    }
                }
            }
        }

        return {
            rootItemId  : rootItemId || ( dataContext && rootItemKey ? dataUtils.findItem( dataContext, 'Form', rootItemKey )?.id ?? 0 : state.rootItemId ),
            isDirty,
            errorCount,
            errorTooltip: errorCount
                ? <div key="error" className="yn-tooltip-error">
                    <div className="yn-tooltip-header">
                        {__( 'YN.base.common.tooltip.error.title' )}
                    </div>
                    {tooltipErrors}
                </div>
                : undefined,
            errorMessage: errorCount
                ? <>
                    <div className="yn-error-message">
                        {__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.text' )}
                    </div>
                    <hr className="yn-error-ruler"/>
                    <div className="yn-error-suberror">
                        {messageErrors}
                    </div>
                </>
                : undefined,
            rootItemFormState,
            erronousLayoutElementIds
        };
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        matchMedia( 'screen and (max-width: 960px)' ).addEventListener( 'change', this.onMatchMobileChangeHandler );

        this.afterRender();

        void this.checkForAiAvailable();

        if ( !this.props.elusiveData ) {
            if ( this.props.rootItemId || this.props.rootItemKey ) {
                void this.loadData();
            }
            else {
                this.checkForCreateNewFormPrerequisits();
            }
        }
        else {
            this.setState( {
                initialized: true
            } );
        }
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        matchMedia( 'screen and (max-width: 960px)' ).removeEventListener( 'change', this.onMatchMobileChangeHandler );
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : Readonly<AllProps>, prevState : Readonly<State> ) {
        const {
                  uid
              } = this.props,
              {
                  createNewFormDialogKey,
                  isDirty,
                  showCreateNewFormDialog
              } = this.state;

        this.afterRender();

        // mark panel dirty
        if ( isDirty !== undefined && isDirty !== prevState.isDirty ) {
            this.props.setPanelDirty( uid, isDirty );
        }

        // key changed or dialog shown
        if (
            prevState.createNewFormDialogKey !== createNewFormDialogKey
            || ( !prevState.showCreateNewFormDialog && showCreateNewFormDialog )
        ) {
            this.setState( { createNewFormDialogKeyError: __( 'YN.base.finder.item.key.check.unique' ) } );
            this.checkKeyUniqueness();
        }
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        return !this.isLoading;
    }

    async loadData() : Promise<boolean> {
        this.isLoading = true;

        const {
                  dataContext,
                  rootItemId,
                  rootItemKey,
                  uid
              }      = this.props,
              retVal = await this.props.loadData?.(
                  'form/loadConfigData',
                  dataContext,
                  dataContext,
                  undefined,
                  'CFG',
                  {
                      params   : rootItemId ? {
                          viewId: rootItemId
                      } : {
                          viewKey: rootItemKey
                      },
                      mergeMode: 'keepExisting' as MergeMode
                  },
                  undefined,
                  resonseData => {
                      if ( resonseData?.data.rootItemId && rootItemId !== resonseData.data.rootItemId ) {
                          this.props.setParam( uid, 'rootItemId', resonseData.data.rootItemId );
                      }

                      if ( !resonseData?.data.items.length ) {
                          this.checkForCreateNewFormPrerequisits();
                      }
                      else {
                          this.setState( {
                              initialized: true
                          } );
                      }
                  }
              );

        this.isLoading = false;

        return retVal;
    }

    afterRender( props : AllProps = this.props as AllProps ) {
        const {
                  uid,
                  dataContext,
                  rootItemId,
                  rootItemKey,
                  intercomMessages
              }                                             = props,
              {
                  isDirty,
                  errorCount
              }                                             = this.state,
              icon                                          = this.getPanelIcon( FormDesignerView.panelTabIcon ),
              itemTitleConfig : ItemTitleConfig | undefined = rootItemId
                  ? {
                      dataContext,
                      id: rootItemId
                  }
                  : undefined,
              title                                         = this.getPanelTitle( FormDesignerView.panelTabTitle, undefined, itemTitleConfig ),
              path                                          = FormDesignerView.buildPanelPathFromProps( { dataContext, rootItemId, rootItemKey } );

        this.checkForIntercomMessages( intercomMessages );

        this.setPanelInfo( uid, icon, title, undefined, isDirty, !!errorCount, path );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage?.( this.props.uid, 'toast', key as string );
            }
        }
    }

    checkForCreateNewFormPrerequisits() {
        const {
                  createNewFormDialogItemType
              } = this.state;

        // no root DataObject ItemType -> show dialog
        if ( !createNewFormDialogItemType ) {
            this.setState( {
                initialized            : true,
                showCreateNewFormDialog: true
            } );
        }
        else {
            this.setState( {
                initialized: true
            } );

            this.createNewForm();
        }
    }

    /**
     * Checks the given key for uniqueness within the given ItemType against the backend.
     *
     * @private
     */
    checkKeyUniqueness() {
        const {
                  uid
              }          = this.props,
              {
                  formItemTypeId,
                  createNewFormDialogKey
              }          = this.state,
              requestUid = 'checkKeyUniqueness';

        if ( formItemTypeId && createNewFormDialogKey ) {

            // abort running check
            if ( this.props.xhrRequestsForComponent?.[ requestUid ] ) {
                xhr.abort( uid, requestUid );
            }

            const doLoad = async () => {
                const params : CheckItemKeyUniquenessParameters = {
                          schema  : 'CFG',
                          status  : 'all',
                          itemType: formItemTypeId,
                          key     : createNewFormDialogKey
                      },
                      response                                  = await xhr.post(
                          `${BASE_URL}/api/data/checkItemKeyUniqueness`,
                          params,
                          undefined,
                          uid,
                          requestUid
                      );

                if ( response?.data ) {
                    const responseData = response.data as CheckItemKeyUniquenessResult;

                    if ( responseData.success && !responseData.data.keyIsUnique ) {
                        this.setState( { createNewFormDialogKeyError: __( 'YN.base.frontend.formDesigner.createNewDialog.key.error.notUnique' ) } );
                    }
                    else {
                        this.setState( { createNewFormDialogKeyError: '' } );
                    }
                }
            };

            doLoad().catch( ( error ) => {
                console.log( error );
            } );
        }
    }

    createNewForm() {
        const {
                  uid,
                  dataContext,
                  referenceToCreateForNew,
                  rootItemName
              }              = this.props,
              {
                  formItemTypeId,
                  createNewFormDialogKey,
                  createNewFormDialogSchema,
                  createNewFormDialogItemType
              }              = this.state,
              activeStatusId = metaModel[ 'CFG' ].getStatus( 'active' )!.id;

        if ( createNewFormDialogKey && createNewFormDialogItemType ) {
            const form           = this.props.addItem( dataContext, {
                      itemTypeId: formItemTypeId,
                      key       : createNewFormDialogKey,
                      name      : rootItemName ?? createNewFormDialogItemType,
                      statusId  : activeStatusId
                  }, 'CFG' ),
                  rootDataObject = this.props.addItem( dataContext, {
                      itemTypeId: metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                      key       : `${createNewFormDialogKey}:${createNewFormDialogItemType}:${uuidV4().substring( 0, 8 )}`,
                      name      : createNewFormDialogItemType,
                      statusId  : activeStatusId,
                      properties: [
                          {
                              propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DataObject', 'ObjectSchema' )!.id,
                              value        : createNewFormDialogSchema.toLowerCase(),
                              statusId     : activeStatusId
                          },
                          {
                              propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DataObject', 'ObjectType' )!.id,
                              value        : createNewFormDialogItemType,
                              statusId     : activeStatusId
                          }
                      ]
                  }, 'CFG' ),
                  container      = this.props.addItem( dataContext, {
                      itemTypeId: metaModel[ 'CFG' ].getItemType( 'Container' )!.id,
                      key       : `${createNewFormDialogKey}:${uuidV4().substring( 0, 8 )}`,
                      name      : 'Container',
                      statusId  : activeStatusId
                  }, 'CFG' ),
                  stringField    = this.props.addItem( dataContext, {
                      itemTypeId: metaModel[ 'CFG' ].getItemType( 'StringField' )!.id,
                      key       : `${createNewFormDialogKey}:${createNewFormDialogItemType}:name:${uuidV4().substring( 0, 8 )}`,
                      name      : `${createNewFormDialogItemType} name`,
                      statusId  : activeStatusId,
                      properties: [
                          {
                              propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'StringField', 'AttributePath' )!.id,
                              value        : 'item.name',
                              statusId     : activeStatusId
                          },
                          {
                              propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'StringField', 'Order' )!.id,
                              value        : 10,
                              statusId     : activeStatusId
                          },
                          {
                              propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'StringField', 'Width' )!.id,
                              value        : 100,
                              statusId     : activeStatusId
                          }
                      ]
                  }, 'CFG' );

            if ( form && rootDataObject && container ) {
                this.props.addReference( dataContext, {
                    srcItemId     : rootDataObject.id,
                    destItemId    : form.id,
                    referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'Form', 'isRootOf', 'src' )![ 0 ].id,
                    statusId      : activeStatusId
                }, 'CFG' );
                this.props.addReference( dataContext, {
                    srcItemId     : container.id,
                    destItemId    : form.id,
                    referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Container', 'Form', 'belongsTo', 'src' )![ 0 ].id,
                    statusId      : activeStatusId
                }, 'CFG' );
                this.props.addReference( dataContext, {
                    srcItemId     : stringField.id,
                    destItemId    : container.id,
                    referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'StringField', 'Container', 'belongsTo', 'src' )![ 0 ].id,
                    statusId      : activeStatusId
                }, 'CFG' );
                this.props.addReference( dataContext, {
                    srcItemId     : rootDataObject.id,
                    destItemId    : stringField.id,
                    referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'StringField', 'providesDataFor', 'src' )![ 0 ].id,
                    statusId      : activeStatusId
                }, 'CFG' );

                // add additionalReference (replace rootItemId by form.id)
                if ( referenceToCreateForNew ) {
                    if ( referenceToCreateForNew.srcItemId === 0 ) {
                        referenceToCreateForNew.srcItemId = form.id;
                    }
                    else {
                        referenceToCreateForNew.destItemId = form.id;
                    }

                    this.props.addReference( dataContext, referenceToCreateForNew, 'CFG' );
                    this.props.setParam( uid, 'referenceToCreateForNew', undefined );
                }

                this.props.setParam( uid, 'rootItemId', form.id );
            }
        }
    }

    onReloadButtonClick() {
        const {
                  uid,
                  dataContext
              } = this.props;

        if ( dataContext ) {
            this.isLoading = true;

            this.props.clearElusiveData?.( uid );
            this.props.clearElusiveDataForSubpanels?.( uid );

            // clean data
            global.data[ dataContext ] = {
                items     : [],
                references: [],
                schema    : global.data[ dataContext ].schema
            };

            this.setState( {}, () => void this.loadData() );
        }
    }

    onNewLayoutElementsWidthChange( newLayoutElementsWidth : string ) {
        this.props.setParam( this.props.uid, 'newLayoutElementsWidth', newLayoutElementsWidth );
    }

    onShowHiddenLayoutElementsChange( showHiddenLayoutElements : boolean ) {
        this.props.setParam( this.props.uid, 'showHiddenLayoutElements', showHiddenLayoutElements );
    }

    onMenuScroll() {
        this.setState( prevState => ( { scrollIndicator: ( prevState.scrollIndicator ?? 0 ) + 1 } ) );
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenuWidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menuWidth', width );
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenu2WidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menu2Width', width );
    }

    onSelectLayoutElement( selectedLayoutElementId? : number ) {
        this.props.setParam?.( this.props.uid, 'selectedLayoutElementId', selectedLayoutElementId );
    }

    onDataRootItemIdChange( id : number ) {
        this.formDesigner?.clearRootItemData();
        this.props.setParam( this.props.uid, 'dataRootItemId', id );
    }

    onCreateNewFormDialogOkButtonClick() {
        const {
                  aiAvailable,
                  selectedQuickActionId
              }            = this.state,
              quickActions = this.getQuickActions(),
              quickAction  = quickActions.find( ( action ) => action.id === selectedQuickActionId );

        if ( aiAvailable && quickAction ) {
            void this.generateFormViaAi( quickAction.prompt );
        }
        else {
            this.createNewForm();
            this.setState( { showCreateNewFormDialog: false } );
        }
    }

    onSelectQuickAction( id : string | undefined ) {
        this.setState( ( prevState ) => ( {
            // allow toggling the selection off (back to "create default form")
            selectedQuickActionId: prevState.selectedQuickActionId === id ? undefined : id
        } ) );
    }

    /**
     * Checks whether ai is available for the form-generation BackendAction and stores the result.
     *
     * @private
     */
    async checkForAiAvailable() {
        const {
                  uid,
                  payloadItemSchema
              } = this.props;

        await this.props.loadData(
            'ai/isAvailable',
            `${uid}_checkForFormAiAvailable`,
            uid,
            'checkForFormAiAvailable',
            payloadItemSchema ?? 'DATA',
            {
                params: {
                    aiChatCompletionKey: FormDesignerView.aiChatCompletionKey
                }
            },
            false,
            ( result ) => {
                if ( result?.data?.available ) {
                    this.setState( { aiAvailable: true } );
                }
            }
        );
    }

    /**
     * Triggers the GenerateForm BackendAction with the given prompt and applies the result.
     *
     * @param {string} prompt
     *
     * @private
     */
    async generateFormViaAi( prompt : string ) {
        const {
                  uid
              } = this.props,
              {
                  createNewFormDialogKey,
                  createNewFormDialogItemType,
                  createNewFormDialogSchema
              } = this.state;

        if ( !createNewFormDialogKey || !createNewFormDialogItemType ) {
            return;
        }

        this.setState( { generatingForm: true } );

        let formCreatedId = 0;

        try {
            await this.props.loadData(
                'backendAction/execute',
                `${uid}_generateForm`,
                uid,
                'generateForm',
                'CFG',
                {
                    params: {
                        backendActionKey: 'GenerateForm',
                        prompt,
                        itemTypeName    : createNewFormDialogItemType,
                        itemTypeSchema  : createNewFormDialogSchema,
                        formKey         : createNewFormDialogKey
                    }
                },
                false,
                ( result ) => {
                    if ( result?.success && result.data ) {
                        formCreatedId = this.applyGeneratedForm( result.data );
                    }
                }
            );
        }
        catch ( e ) {
            formCreatedId = 0;
        }

        if ( formCreatedId ) {
            this.props.setParam( uid, 'rootItemId', formCreatedId );

            this.setState( {
                generatingForm         : false,
                showCreateNewFormDialog: false
            } );
        }
        else {
            this.setState( { generatingForm: false } );

            this.toast?.show( {
                severity: 'error',
                summary : __( 'YN.base.frontend.formDesigner.generateForm.error.title' ),
                detail  : __( 'YN.base.frontend.formDesigner.generateForm.error.message' ),
                life    : 5000
            } );
        }
    }

    /**
     * Converts a generated form config (LoadedItemData[] / LoadedReferenceData[] returned by the
     * GenerateForm BackendAction) into live design data by calling addItem / addReference.
     *
     * The BackendAction has already resolved all CFG metaModel ids and assigned negative temp ids
     * for the new objects. As negative ids are not allowed when persisting, all ids are stripped
     * here (mirroring FormDesigner onDrop) and the new item ids are remapped on the references.
     *
     * @param {Object} formConfig  { items: LoadedItemData[], references: LoadedReferenceData[] }
     *
     * @return {boolean} whether a root form was created
     *
     * @private
     */
    applyGeneratedForm( formConfig : { items? : LoadedItemData[]; references? : LoadedReferenceData[] } ) : number {
        const {
                  uid,
                  dataContext,
                  referenceToCreateForNew
              }                                                       = this.props,
              items                                                   = Array.isArray( formConfig?.items ) ? formConfig.items : [],
              references                                              = Array.isArray( formConfig?.references ) ? formConfig.references : [],
              formItemTypeId                                          = metaModel[ 'CFG' ].getItemType( 'Form' )?.id,
              // maps the negative temp item id from the BackendAction to the freshly created item id
              oldToNewItemIdMapping : { [ oldId : string ] : number } = {};

        if ( !items.length ) {
            return 0;
        }

        let newForm : Item | undefined = undefined;

        // 1) create all items (strip negative ids so fresh ids get assigned)
        for ( const itemData of items ) {
            const oldId                           = itemData.id,
                  itemAttributes : ItemAttributes = itemData;

            if ( ( itemAttributes.id ?? 0 ) < 0 ) {
                delete itemAttributes.id;
            }
            for ( const property of itemAttributes.properties ?? [] ) {
                if ( ( property.id ?? 0 ) < 0 ) {
                    delete property.id;
                }
            }
            for ( const enumProperty of itemAttributes.enumProperties ?? [] ) {
                for ( const property of enumProperty.properties ?? [] ) {
                    if ( ( property.id ?? 0 ) < 0 ) {
                        delete property.id;
                    }
                }
            }

            const newItem = this.props.addItem( dataContext, itemAttributes as ItemAttributesForCreation, 'CFG' );

            if ( newItem ) {
                if ( ( oldId ?? 0 ) < 0 ) {
                    oldToNewItemIdMapping[ String( oldId ) ] = newItem.id;
                }
                if ( itemData.itemTypeId === formItemTypeId ) {
                    newForm = newItem;
                }
            }
        }

        // 2) create all references (remap item ids + strip negative ids)
        for ( const referenceData of references ) {
            const referenceAttributes : ReferenceAttributes = referenceData;

            if ( oldToNewItemIdMapping[ String( referenceAttributes.srcItemId ) ] !== undefined ) {
                referenceAttributes.srcItemId = oldToNewItemIdMapping[ String( referenceAttributes.srcItemId ) ];
            }
            if ( oldToNewItemIdMapping[ String( referenceAttributes.destItemId ) ] !== undefined ) {
                referenceAttributes.destItemId = oldToNewItemIdMapping[ String( referenceAttributes.destItemId ) ];
            }

            if ( ( referenceAttributes.id ?? 0 ) < 0 ) {
                delete referenceAttributes.id;
            }
            for ( const property of referenceAttributes.properties ?? [] ) {
                if ( ( property.id ?? 0 ) < 0 ) {
                    delete property.id;
                }
            }
            for ( const enumProperty of referenceAttributes.enumProperties ?? [] ) {
                for ( const property of enumProperty.properties ?? [] ) {
                    if ( ( property.id ?? 0 ) < 0 ) {
                        delete property.id;
                    }
                }
            }

            this.props.addReference( dataContext, referenceAttributes as ReferenceAttributesForCreation, 'CFG' );
        }

        // 3) wire up the new root form
        if ( newForm ) {
            // add additionalReference (replace rootItemId by form.id)
            if ( referenceToCreateForNew ) {
                if ( referenceToCreateForNew.srcItemId === 0 ) {
                    referenceToCreateForNew.srcItemId = newForm.id;
                }
                else {
                    referenceToCreateForNew.destItemId = newForm.id;
                }

                this.props.addReference( dataContext, referenceToCreateForNew, 'CFG' );
                this.props.setParam( uid, 'referenceToCreateForNew', undefined );
            }
        }

        return newForm?.id ?? 0;
    }

    onCreateNewFormDialogHide() {
        this.props.closePanel( this.props.uid );
    }

    onCreateNewFormDialogSetKey( value : string | undefined ) {
        let key      = '',
            keyError = '';

        if ( value !== undefined ) {
            key = value;

            if ( !key ) {
                keyError = __( 'YN.base.finder.item.key.error.empty' );
            }
            else if ( / /.test( key ) ) {
                keyError = __( 'YN.base.finder.item.key.error.whitespaces' );
            }

            this.setState( { createNewFormDialogKey: key, createNewFormDialogKeyError: keyError } );
        }
    }

    onCreateNewFormDialogSetSchema( value : string ) {
        const {
                  createNewFormDialogItemType,
                  createNewFormDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewFormDialogSchema === 'CFG'
                  ? `CFG:${createNewFormDialogItemType ?? ''}`
                  : createNewFormDialogItemType ?? '';

        let {
                createNewFormDialogKey,
                createNewFormDialogKeyError
            } = this.state;

        if ( !createNewFormDialogKey || createNewFormDialogKey === oldGeneratedKey ) {
            createNewFormDialogKey = value === 'CFG' ? 'CFG:' : '';
            createNewFormDialogKeyError = undefined;
        }

        this.setState( {
            createNewFormDialogSchema  : value as Schema,
            createNewFormDialogItemType: undefined,
            createNewFormDialogKey,
            createNewFormDialogKeyError
        } );
    }

    onCreateNewFormDialogSetItemType( value : string ) {
        const {
                  createNewFormDialogItemType,
                  createNewFormDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewFormDialogSchema === 'CFG'
                  ? `CFG:${createNewFormDialogItemType ?? ''}`
                  : createNewFormDialogItemType ?? '';

        let {
                createNewFormDialogKey,
                createNewFormDialogKeyError
            } = this.state;

        if ( !createNewFormDialogKey || createNewFormDialogKey === oldGeneratedKey ) {
            createNewFormDialogKey = createNewFormDialogSchema === 'CFG' ? 'CFG:' + value : value;
            createNewFormDialogKeyError = undefined;
        }

        this.setState( {
            createNewFormDialogItemType: value,
            createNewFormDialogKey,
            createNewFormDialogKeyError
        } );
    }

    onNewLayoutElementDragStart( rootLayoutElementId : number, data : DragData, options : DragOptions, e : React.DragEvent<HTMLElement> ) : void {
        this.formDesigner?.startDrag( rootLayoutElementId, data, options, e );
    }

    onMarkLayoutElementsForAlias( alias : ( string | undefined ) ) : void {
        this.formDesigner?.markLayoutElementsForAlias( alias );
    }

    onMarkFieldsForAlias( alias : ( string | undefined ) ) : void {
        this.tree?.markFieldsForAlias( alias );
    }

    onMatchMobileChangeHandler() {
        this.setState( { mobile: getMobile() } );
    }

    getSelectableSchemata( user? : User ) {
        const retVal : Schema[] = [];

        for ( const schema of schemata ) {
            const itemTypes = metaModel[ schema ].getItemTypes() ?? [];

            for ( const itemType of itemTypes ) {
                if (
                    itemType.id > 0
                    && user?.hasGenericPermissionOnItemType( 'read', itemType.id, schema )
                ) {
                    retVal.push( schema );
                    break;
                }
            }
        }

        retVal.sort();

        return retVal;
    }

    getQuickActions() : { id : string; label : string; prompt : string }[] {
        return [
            {
                id    : 'standard',
                label : __( 'YN.base.frontend.formDesigner.quickActions.standard.label' ),
                prompt: __( 'YN.base.frontend.formDesigner.quickActions.standard.prompt' )
            },
            {
                id    : 'twoColumnCards',
                label : __( 'YN.base.frontend.formDesigner.quickActions.twoColumnCards.label' ),
                prompt: __( 'YN.base.frontend.formDesigner.quickActions.twoColumnCards.prompt' )
            },
            {
                id    : 'requiredOnly',
                label : __( 'YN.base.frontend.formDesigner.quickActions.requiredOnly.label' ),
                prompt: __( 'YN.base.frontend.formDesigner.quickActions.requiredOnly.prompt' )
            },
            {
                id    : 'tabs',
                label : __( 'YN.base.frontend.formDesigner.quickActions.tabs.label' ),
                prompt: __( 'YN.base.frontend.formDesigner.quickActions.tabs.prompt' )
            },
            {
                id    : 'compact',
                label : __( 'YN.base.frontend.formDesigner.quickActions.compact.label' ),
                prompt: __( 'YN.base.frontend.formDesigner.quickActions.compact.prompt' )
            }
        ];
    }

    getCreateNewFormDialog() {
        const {
                  uid,
                  user
              }                           = this.props,
              {
                  aiAvailable,
                  createNewFormDialogItemType,
                  createNewFormDialogKey,
                  createNewFormDialogKeyError,
                  createNewFormDialogSchema,
                  generatingForm,
                  selectedQuickActionId,
                  showCreateNewFormDialog
              }                           = this.state,
              quickActions                = this.getQuickActions(),
              okDisabled                  = Boolean( !createNewFormDialogKey || !createNewFormDialogItemType || generatingForm ),
              selectableSchemata          = this.getSelectableSchemata( user ),
              style : React.CSSProperties = {
                  width            : '33rem',
                  minWidth         : '33rem',
                  [ '--min-width' ]: '33rem',
                  minHeight        : '25vh'
              };

        // only one selectable schemata and not set -> set it
        if ( selectableSchemata.length === 1 && createNewFormDialogSchema !== selectableSchemata[ 0 ] ) {
            this.setState( { createNewFormDialogSchema: selectableSchemata[ 0 ] } );
            return;
        }

        return <Dialog
            key={`${uid}_createNewFormDialog`}
            id={`${uid}_createNewFormDialog`}

            visible={showCreateNewFormDialog}
            onHide={this.onCreateNewFormDialogHide}
            focusOnShow={false}
            closeOnEscape={false}
            className="yn-form-dialog"
            style={style}
            appendTo={( document.querySelector( '.yn-tabpanel.p-tabview-panel' ) ?? undefined ) as HTMLElement | undefined}
            header={__( 'YN.base.frontend.formDesigner.createNewDialog.title' )}
            footer={<div>
                <Button
                    id={`${uid}_okButton`}
                    key="okButton"
                    label={aiAvailable && selectedQuickActionId ? __( 'YN.base.frontend.formDesigner.generateForm.label' ) : __( 'YN.base.common.ok' )}
                    icon={aiAvailable && selectedQuickActionId ? 'pi pi-bolt' : undefined}
                    className="p-button-outlined mr-3"
                    disabled={okDisabled}
                    loading={generatingForm}
                    onClick={this.onCreateNewFormDialogOkButtonClick}
                />
                <Button
                    id={`${uid}_cancelButton`}
                    key="cancelButton"
                    label={__( 'YN.base.common.cancel' )}
                    className="p-button-secondary p-button-outlined"
                    onClick={this.onCreateNewFormDialogHide}
                />
            </div>}
        >
            <StringField
                uid={`${uid}_keyField`}
                label={__( 'YN.base.frontend.formDesigner.createNewDialog.key.label' )}
                value={createNewFormDialogKey}
                errorMessage={createNewFormDialogKeyError}
                disabled={generatingForm}
                style={{ width: '30rem' }}
                labelPosition="top"
                infoTextLayout="hidden"
                markDirty={false}
                onChange={this.onCreateNewFormDialogSetKey}
            />
            <FieldWrapper
                uid={`${uid}_itemTypeField`}
                label={__( 'YN.base.frontend.formDesigner.createNewDialog.itemType.label' )}
                labelPosition="top"
                infoTextLayout="hidden"
                isDirty={false}
                autoComplete={false}
            >
                {selectableSchemata.length > 1 &&
                    <SchemaDropdown
                        uid={uid}
                        value={createNewFormDialogSchema}
                        onChange={this.onCreateNewFormDialogSetSchema}

                        style={{ flex: '0 0 7rem', width: '7rem', paddingRight: '.5rem' }}
                        disabled={generatingForm}
                    />
                }
                <ItemTypeDropdown
                    uid={uid}

                    schema={createNewFormDialogSchema}
                    value={createNewFormDialogItemType}
                    rawMode={false}
                    showAdditionalRawNames={false}
                    markDirty={false}
                    onChange={this.onCreateNewFormDialogSetItemType}
                    inputStyle={{ width: selectableSchemata.length > 1 ? '23rem' : '30rem' }}
                    disabled={generatingForm}
                />
            </FieldWrapper>
            {aiAvailable && createNewFormDialogSchema === 'DATA' &&
                <>
                    <label className="yn-menu-subtitle-label block">{__( 'YN.base.frontend.formDesigner.generateForm.title' )}</label>
                    <FieldWrapper
                        uid={`${uid}_generateFormFieldText`}
                        labelPosition="top"
                        labelVisibility="hidden"
                        infoTextLayout="hidden"
                        isDirty={false}
                        autoComplete={false}
                    >
                        {__( 'YN.base.frontend.formDesigner.generateForm.text' )}
                    </FieldWrapper>
                    <FieldWrapper
                        uid={`${uid}_generateFormFieldRadio`}
                        labelPosition="top"
                        labelVisibility="hidden"
                        infoTextLayout="hidden"
                        isDirty={false}
                        autoComplete={false}
                    >
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem' }}>
                            {quickActions.map( ( quickAction ) => (
                                <div key={quickAction.id} style={{ display: 'flex', alignItems: 'center' }}>
                                    <RadioButton
                                        inputId={`${uid}_quickAction_${quickAction.id}`}
                                        name="quickAction"
                                        value={quickAction.id}
                                        checked={selectedQuickActionId === quickAction.id}
                                        disabled={generatingForm}
                                        onChange={() => this.onSelectQuickAction( quickAction.id )}
                                    />
                                    <label htmlFor={`${uid}_quickAction_${quickAction.id}`} className="ml-2" style={{ cursor: 'pointer' }}>
                                        {quickAction.label}
                                    </label>
                                </div>
                            ) )}
                        </div>
                    </FieldWrapper>
                </>
            }
        </Dialog>;
    }

    getHeaderbar( uid : string, formKey : string, formName : string, rootDataObjectItemType : string, rootDataObjectSchema : Schema ) {
        return <Headerbar
            key={`${uid}_headerbar`}

            uid={uid}

            formKey={formKey}
            formName={formName}

            rootDataObjectItemType={rootDataObjectItemType}
            rootDataObjectSchema={rootDataObjectSchema}
        />;
    }

    getToolbar( uid : string, dataContext : string, rootItem : Item, dataRootItemId : number | undefined, dataItemType : string, dataSchema : Schema, mode : FormDesignerMode, readOnly : boolean, saveButtonErrorCount : number, saveButtonErrorTooltip : React.ReactNode | React.ReactNode[], newLayoutElementsWidth : string, showHiddenLayoutElements : boolean, hasHiddenLayoutElements : boolean | undefined, isDirty : boolean ) {
        return <Toolbar
            key={`${uid}_ItemFinderToolbar`}

            uid={uid}

            dataContext={dataContext}
            schema="CFG"

            rootItemId={rootItem?.id}

            dataRootItemId={dataRootItemId}
            dataItemType={dataItemType}
            dataSchema={dataSchema}
            onDataRootItemIdChange={this.onDataRootItemIdChange}

            treeViewItem={rootItem}

            mode={mode}

            saveButtonDisabled={readOnly}
            saveButtonErrorCount={saveButtonErrorCount}
            saveButtonErrorTooltip={saveButtonErrorTooltip}
            deleteButtonDisabled={readOnly}
            newLayoutElementsWidth={newLayoutElementsWidth}
            showHiddenLayoutElements={showHiddenLayoutElements}
            showModifiedHint={isDirty}

            onNewLayoutElementsWidthChange={this.onNewLayoutElementsWidthChange}
            onShowHiddenLayoutElementsChange={this.onShowHiddenLayoutElementsChange}
            onReloadButtonClick={this.onReloadButtonClick}

            showHiddenLayoutElementsCheckbox={mode === 'design' && hasHiddenLayoutElements}
        />;
    }

    getDesignerTree( uid : string, dataContext : string, treeProps : any, parentUid : string, rootItemId : number, readOnly : boolean, additionalElusiveDataContexts : string[], selectedLayoutElementId : number | undefined, newLayoutElementsWidth : string, repeatedDataObjects? : number[] ) {
        return <DesignerTree
            key={uid}

            ref={( el ) => this.tree = el}

            {...treeProps}

            uid={uid}
            dataContext={dataContext}
            parentUid={parentUid}
            rootItemId={rootItemId}

            readOnly={readOnly}

            selectedLayoutElementId={selectedLayoutElementId}
            newLayoutElementsWidth={newLayoutElementsWidth}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            repeatedDataObjects={repeatedDataObjects}

            onSelectLayoutElement={this.onSelectLayoutElement}
            onNewLayoutElementDragStart={this.onNewLayoutElementDragStart}
            onMarkLayoutElementsForAlias={this.onMarkLayoutElementsForAlias}

            className="yn-sidebar-content p-tree"
        />;
    }

    getDesignerTreeEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getFormDesigner( configUid : string, configDataContext : string, uid : string, formDesignerProps : any, mode : FormDesignerMode, showHiddenLayoutElements : boolean, dataContext : string, configRootItemId : number, readOnly : boolean, additionalElusiveDataContexts : string[], rootItemFormState : State['rootItemFormState'], erronousLayoutElementIds : State['erronousLayoutElementIds'], rootItemId? : number, selectedLayoutElementId? : number ) {
        return <FormDesigner
            key={uid}

            ref={( el ) => this.formDesigner = el}

            {...formDesignerProps}

            uid={uid}
            configUid={configUid}

            mode={mode}
            showHiddenLayoutElements={showHiddenLayoutElements}
            readOnly={readOnly}

            configDataContext={configDataContext}
            configRootItemId={configRootItemId}

            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            rootItemId={rootItemId}
            rootItemFormState={rootItemFormState}
            erronousLayoutElementIds={erronousLayoutElementIds}

            selectedLayoutElementId={selectedLayoutElementId}
            onMarkFieldsForAlias={this.onMarkFieldsForAlias}
            onSelectLayoutElement={this.onSelectLayoutElement}
        />;
    }

    getFormDesignerEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getForm( uid : string, dataContext : string, formProps : any, additionalElusiveDataContexts : string[], viewKey : string, rootItemId : number, readOnly : boolean, treeParentItemId : number, treeRootItemId : number, treeVariables : Variables | undefined ) {
        return <Form
            key={uid}

            {...formProps}

            uid={uid}
            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            schema="CFG"
            viewKey={viewKey}
            mode="embedded"
            readOnly={readOnly}
            rootItemId={rootItemId}
            rootItemKey=""
            rootItemType=""
            treeParentItemId={treeParentItemId}
            treeRootItemId={treeRootItemId}
            treeVariables={treeVariables}
            treeSetVariables={this.onSetSidePanelTreeVariables}
        />
    }

    /**
     * Tree variables the *side panel* wrote back.
     *
     * A side panel is a form of its own, and what it writes into `treeVariables` is meant to survive
     * the switch to the next selected element -- the active tab of the panel, for instance, which
     * `Common:Action:SetActiveTabIndex` stores there so the panel does not jump back to the first tab
     * on every click in the canvas. Without a `treeSetVariables` handler the write goes into a copy
     * ( `Form.onTriggerAction` ) and is dropped, so the value was never seen again.
     *
     * Only the keys the panel actually changed are kept: everything the designer supplies
     * ( data structure, layout elements, inherited props ) is rebuilt on every render and must not
     * be frozen here.
     */
    sidePanelTreeVariables : Variables = {};

    /** the object handed to the side panel last, to tell the panel's own keys from the designer's */
    lastSidePanelTreeVariables : Variables = {};

    /** what the designer supplies, plus what the side panel wrote back earlier */
    getSidePanelTreeVariables( fromDesigner : Variables ) : Variables {
        this.lastSidePanelTreeVariables = { ...fromDesigner, ...this.sidePanelTreeVariables };

        return this.lastSidePanelTreeVariables;
    }

    onSetSidePanelTreeVariables( variables : Variables ) {
        const provided = this.lastSidePanelTreeVariables,
              own : Variables = { ...this.sidePanelTreeVariables };

        let changed = false;

        for ( const key of Object.keys( variables ?? {} ) ) {
            if ( variables[ key ] !== provided[ key ] ) {
                own[ key ] = variables[ key ];
                changed    = true;
            }
        }

        if ( changed ) {
            this.sidePanelTreeVariables = own;
        }
    }

    getFormEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    /**
     * React render method
     * @return {*}
     */
    render() {
        const {
                  dataContext,
                  dataRootItemId,
                  elusiveData,
                  menu2Width,
                  menuWidth,
                  mode,
                  newLayoutElementsWidth,
                  selectedLayoutElementId,
                  showHiddenLayoutElements,
                  status,
                  subPanelProps,
                  timestamp,
                  timezone,
                  uid,
                  user
              }                           = this.props as AllProps,
              {
                  erronousLayoutElementIds,
                  errorCount,
                  errorMessage,
                  errorTooltip,
                  initialized,
                  isDirty,
                  rootItemFormState,
                  rootItemId = 0,
                  showCreateNewFormDialog
              }                           = this.state,
              rootItem                    = global.data[ dataContext ]?.items?.[ rootItemId ],
              dataStructure               = this.formDesigner?.dataStructure,
              dataObjectItems             = dataStructure
                  ? extractDataObjectsFromDataStructure( dataStructure )
                      .map( dO => global.data[ dataContext ]?.items?.[ dO.origId ] )
                      .filter( item => !!item )
                  : [],
              layoutElementItems          = Object.keys( this.formDesigner?.layoutElements ?? {} ).map( layoutElementId => global.data[ dataContext ]?.items?.[ layoutElementId ] ),
              repeatedDataObjects         = this.formDesigner?.repeatedDataObjects,
              rootDataContext             = `${dataContext}_data`,
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              readOnly                    = !( user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) || !rootItem?.properties.get( metaModel[ 'CFG' ].getPropertyType( 'SystemRelevant' )?.id ?? 0 )?.value ),
              rawMode                     = false,
              showAdditionalRawNames      = false,
              selectedLayoutElement       = selectedLayoutElementId ? global.data[ dataContext ]?.items[ selectedLayoutElementId ] : undefined,
              currentItemId               = selectedLayoutElement?.id ?? rootItemId,
              currentItem                 = selectedLayoutElement ?? rootItem,
              panelConfigs                = user && currentItem ? user.getPanelPropsForItemType( 'CFG', currentItem.itemTypeId, undefined, 'Form' ) as FormPanelProps[] : undefined,
              panelConfig                 = panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^FdSp_/ ) ) ?? panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^XdSp_/ ) ) ?? panelConfigs?.[ 0 ] ?? null,
              allBelongsToReferenceSetIds = currentItem ? metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( currentItem.itemTypeId, '*', 'belongsTo', 'src' )?.map( rset => rset.id ) : undefined,
              parentPayloadId             = dataUtils.findReferences( dataContext, allBelongsToReferenceSetIds, undefined, currentItemId, true )?.[ 0 ]?.destItemId ?? rootItemId,
              designerTreeUid             = `tree-${rootItemId}`,
              fullDesignerTreeUid         = `${uid}_${designerTreeUid}`,
              formUid                     = `form-${currentItemId}`,
              fullFormUid                 = `${uid}_${formUid}`,
              formDesignerUid             = `formDesigner-${rootItemId}`,
              fullFormDesignerUid         = `${uid}_${formDesignerUid}`,
              attributesUid               = `attributes-${currentItemId}`,
              fullAttributesUid           = `${uid}_${attributesUid}`;

        return <>
            {showCreateNewFormDialog &&
                this.getCreateNewFormDialog()
            }
            {elusiveData?.showCouldNotSavePopup &&
                <Dialog
                    key={`${uid}_dialog`}
                    header={__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.title' )}
                    footer={<div><Button label="OK" className="p-button-outlined"
                                         onClick={() => this.props.setElusiveData( uid, { showCouldNotSavePopup: false } )}/></div>}
                    maskClassName="yn-dialog-panel-modal"
                    appendTo="self"
                    position="top"
                    visible={true}
                    style={{ width: 'auto', maxWidth: '80%' }}
                    closable={false}
                    className="yn-error-dialog"
                    onHide={() => {
                    }}>
                    {errorMessage}
                </Dialog>
            }
            {this.getHeaderbar(
                uid,
                rootItem?.key ?? '',
                rootItem?.name ?? '',
                dataStructure?.item.objectType ?? '',
                ( dataStructure?.item.objectSchema ?? 'DATA' ).toUpperCase() as Schema
            )}
            {this.getToolbar(
                uid,
                dataContext,
                rootItem,
                dataStructure ? dataRootItemId : undefined,
                dataStructure?.item.objectType ?? '',
                ( dataStructure?.item.objectSchema ?? 'DATA' ).toUpperCase() as Schema,
                mode,
                readOnly,
                errorCount,
                errorTooltip,
                newLayoutElementsWidth,
                showHiddenLayoutElements,
                this.formDesigner?.hasHiddenLayoutElements,
                isDirty
            )}
            <LoadingMaskPanel forComponent={uid} className="yn-flex-layout-parent">
                <Toast
                    ref={( el ) => this.toast = el}

                    key={`${uid}_toast`}

                    position="bottom-right"
                    style={{ position: 'absolute', marginTop: '0' }}
                />
                <ResizeableLeftMenuPanel
                    key={`${uid}_menuContentPanel`}

                    uid={uid}

                    menuWidth={menuWidth}
                    minMenuWidth={15}
                    maxMenuWidth={25}
                    menuClassName={mode === 'input' ? 'hidden' : undefined}
                    gutterSize={mode === 'input' ? 0 : undefined}
                    contentStyle={this.menuContentPanelContentStyle}
                    onMenuScroll={this.onMenuScroll}
                    onMenuWidthChange={this.onMenuWidthChange}

                    pt={mode === 'input'
                        ? this.menuContentPanelsPt
                        : undefined}

                    menu={initialized && !showCreateNewFormDialog
                        ? this.getDesignerTree(
                            fullDesignerTreeUid,
                            dataContext,
                            subPanelProps?.[ designerTreeUid ],
                            uid,
                            rootItemId,
                            readOnly,
                            [ fullFormDesignerUid, uid ],
                            selectedLayoutElementId,
                            newLayoutElementsWidth,
                            repeatedDataObjects
                        )
                        : <></>
                    }
                    content={<div className="yn-flex-layout-parent" style={{ width: '100%' }}>
                        <ResizeableRightMenuPanel
                            key={`${uid}_menu2ContentPanel`}

                            uid={uid}

                            menuWidth={menu2Width}
                            minMenuWidth={15}
                            maxMenuWidth={33}
                            menuClassName={`yn-content-panel-container yn-menu-form yn-formdesigner-properties-panel ${mode === 'input' ? 'hidden' : ''}`}
                            gutterSize={mode === 'input' ? 0 : undefined}
                            contentClassName={( mode === 'input' ) ? '' : undefined}
                            onMenuWidthChange={this.onMenu2WidthChange}

                            pt={mode === 'input'
                                ? this.menuContentPanelsPt
                                : undefined}

                            content={<LoadingMaskPanel forComponent={fullFormDesignerUid}
                                                       className="yn-flex-layout-parent yn-content-panel-container"
                                                       style={{ position: 'absolute', width: '100% !important', height: '100% !important', padding: 'calc(.5rem - 1px) calc(1.5rem - 1px) !important' }}>
                                {initialized && !showCreateNewFormDialog
                                    ? this.getFormDesigner(
                                        uid,
                                        dataContext,
                                        fullFormDesignerUid,
                                        subPanelProps?.[ formDesignerUid ],
                                        mode,
                                        showHiddenLayoutElements,
                                        rootDataContext,
                                        rootItemId,
                                        readOnly,
                                        [ uid + '_DataObjects', fullDesignerTreeUid, uid ],
                                        rootItemFormState,
                                        erronousLayoutElementIds,
                                        dataRootItemId,
                                        selectedLayoutElementId ?? currentItemId
                                    )
                                    : undefined
                                }
                            </LoadingMaskPanel>}
                            menu={initialized && !showCreateNewFormDialog
                                ? <LoadingMaskPanel key={`${uid}_formview_loadingmask`}
                                                    forComponent={panelConfig?.panelType === 'Form' ? fullFormUid : fullAttributesUid}
                                                    className="yn-flex-layout-parent">
                                    <div>
                                        {panelConfig?.panelType === 'Form' && currentItemId
                                            ? this.getForm(
                                                fullFormUid,
                                                dataContext,
                                                subPanelProps?.[ formUid ],
                                                [ uid + '_DataObjects', fullDesignerTreeUid, fullFormDesignerUid, uid ],
                                                panelConfig.viewKey,
                                                currentItemId,
                                                readOnly,
                                                parentPayloadId,
                                                rootItemId,
                                                this.getSidePanelTreeVariables( {
                                                    dataStructure,
                                                    dataObjectItems,
                                                    layoutElementItems,
                                                    inheritedProps: this.formDesigner?.layoutElementsInheritedProps?.[ currentItemId ],
                                                    fieldConfig   : this.formDesigner?.layoutElementsProps?.[ currentItemId ]
                                                } )
                                            )
                                            : undefined
                                        }
                                        {panelConfig?.panelType !== 'Form' && currentItem &&
                                            <div key={`${uid}_formview`}>
                                                <AttributesForm
                                                    key={fullAttributesUid}

                                                    {...subPanelProps?.[ attributesUid ]}

                                                    uid={fullAttributesUid}
                                                    dataContext={dataContext}
                                                    style={{ padding: 0 }}
                                                    schema="CFG"
                                                    status={status}
                                                    timestamp={timestamp}
                                                    timezone={timezone}
                                                    item={currentItem}
                                                    rawMode={rawMode}
                                                    showAdditionalRawNames={showAdditionalRawNames}
                                                    editable
                                                />
                                            </div>
                                        }
                                    </div>
                                </LoadingMaskPanel>
                                : <></>
                            }
                        />
                    </div>}
                />
            </LoadingMaskPanel>
        </>;
    }
}


// redux stuff

/**
 *
 * @param state
 * @param ownProps
 */
function mapStateToProps( state : StoreState, ownProps : AllProps ) {
    const retVal : Partial<Props> = {
        allElusiveData  : undefined,
        elusiveData     : undefined,
        intercomMessages: undefined,
        user            : undefined
    };

    retVal.allElusiveData = state.elusiveData;

    if ( ownProps.uid ) {

        // get elusiveData
        if ( state.elusiveData?.[ ownProps.uid ] ) {
            retVal.elusiveData = state.elusiveData[ ownProps.uid ];
        }

        // get intercom messages
        if ( state.intercom?.[ ownProps.uid ] ) {
            retVal.intercomMessages = state.intercom[ ownProps.uid ];
        }

        // get the running xhr request belonging to this component (uid)
        if ( state.xhr?.[ ownProps.uid ] ) {
            retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
        }
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * Bind action creators to component props
 * @param dispatch
 * @return {*}
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect( mapStateToProps, mapDispatchToProps )( FormDesignerView );
